/**
 * Where each platform's numbers come from, in two layers.
 *
 * The grid answers the question people actually arrive with — "why is TikTok's
 * engagement zero, is it broken?" — at a glance and without API vocabulary. The
 * expandable panel underneath carries the field names for whoever needs to
 * reconcile a figure against the platform's own reporting.
 *
 * Data comes from services/mediaApi/fieldMapping.ts, which fieldMapping.test.ts
 * checks against the client source on every run — so a renamed API field or a
 * metric that starts being populated breaks a test rather than quietly leaving
 * this page lying.
 *
 * @module components/help/PlatformFieldMap
 */

import React from 'react';
import type { TFunction } from 'i18next';
import { PLATFORM_FIELD_MAPPINGS, METRIC_KEYS, isAbsent, type MetricKey } from '@/services/mediaApi/fieldMapping';
import { useTranslation } from 'react-i18next';

/**
 * Our name for each metric, resolved at render (metricHelpRegistry's rule).
 *
 * The PLATFORM field names rendered beside these (costMicros, action_values,
 * cvPurchaseValue) are deliberately NOT translated — they are the identifiers a
 * reader will search for in the platform's own console, and that is the entire
 * point of this table.
 */
const METRIC_LABELS: Record<MetricKey, (t: TFunction) => string> = {
  impressions: (t) => t('help.metricImpressions'),
  clicks: (t) => t('help.metricClicks'),
  spend: (t) => t('help.metricSpend'),
  conversions: (t) => t('help.metricConversions'),
  conversion_value: (t) => t('help.metricConversionValue'),
  reach: (t) => t('help.metricReach'),
  engagements: (t) => t('help.metricEngagements'),
  video_views: (t) => t('help.metricVideoViews'),
};

export const PlatformFieldMap: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              <th scope="col" className="px-4 py-2 text-left font-semibold text-ink-900">
                {t('help.fieldMap.platform')}
              </th>
              {METRIC_KEYS.map((key) => (
                <th key={key} scope="col" className="px-3 py-2 text-center font-semibold text-ink-900 whitespace-nowrap">
                  {METRIC_LABELS[key](t)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PLATFORM_FIELD_MAPPINGS.map((m) => (
              <tr key={m.platform} className="border-b border-neutral-100">
                <th scope="row" className="px-4 py-2 text-left font-medium text-ink-900 whitespace-nowrap">
                  {m.label}
                </th>
                {METRIC_KEYS.map((key) => {
                  const absent = isAbsent(m, key);
                  return (
                    <td key={key} className="px-3 py-2 text-center" title={m.metrics[key].note?.(t)}>
                      <span
                        aria-label={`${m.label} ${METRIC_LABELS[key](t)}: ${absent ? t('help.fieldMap.absent') : t('help.fieldMap.present')}`}
                        className={absent ? 'text-neutral-700' : 'text-green-700'}
                      >
                        {absent ? '—' : '✓'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-neutral-700">{t('help.fieldMap.legend')}</p>

      {/* Reach is the one column here that reads as a headcount and is not one.
          The invariant in pageInvariants.ts lets this page name it only while this
          sentence is on screen with it. */}
      <p className="text-xs text-neutral-700">{t('help.fieldMap.reach')}</p>

      {PLATFORM_FIELD_MAPPINGS.map((m) => (
        <details key={m.platform} className="rounded-lg border border-neutral-200 bg-white">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-ink-900">
            {t('help.fieldMap.summary', { platform: m.label })}
          </summary>
          <div className="border-t border-neutral-100 px-4 py-3 space-y-3">
            <ul className="space-y-1 text-xs text-neutral-700">
              {m.caveats.map((caveat) => caveat(t)).map((c) => (
                <li key={c}>• {c}</li>
              ))}
            </ul>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-neutral-700">
                  <th scope="col" className="py-1 font-medium">
                    {t('help.fieldMap.metric')}
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    {t('help.fieldMap.field')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {METRIC_KEYS.map((key) => {
                  const note = m.metrics[key].note?.(t);
                  return (
                    <tr key={key} className="border-t border-neutral-100 align-top">
                      <td className="py-1.5 pr-4 whitespace-nowrap text-ink-900">{METRIC_LABELS[key](t)}</td>
                      <td className="py-1.5">
                        {isAbsent(m, key) ? (
                          <span className="text-neutral-700">{t('help.fieldMap.notSent')}</span>
                        ) : (
                          <span className="font-mono text-ink-900">
                            {m.metrics[key].fields.join(` ${t('help.fieldMap.or')} `)}
                          </span>
                        )}
                        {note && <div className="mt-0.5 text-neutral-700">{note}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
};

export default PlatformFieldMap;
